import { menuSchema } from '~/schemas'
import { fetchApi } from './helpers'

const ids = ['main-menu', 'footer-primary', 'footer-secondary'] as const

const normalizeMenuUrl = (url?: string | null) => {
  if (typeof url !== 'string') {
    return null
  }

  const trimmedUrl = url.trim()

  if (!trimmedUrl) {
    return null
  }

  if (/^https?:\/(?!\/)/i.test(trimmedUrl)) {
    return trimmedUrl.replace(/^https?:\/(?!\/)/i, (match) =>
      `${match}/`,
    )
  }

  return trimmedUrl
}

const isExternalUrl = (url?: string | null) =>
  typeof url === 'string' && /^https?:\/\//i.test(url)

const createMenu = (items?: Record<string, any>[]) => {
  if (!items) {
    return
  }

  const addChildren = (parentItem: Record<string, any>) => {
    items.forEach((item) => {
      if (item.parentId === parentItem.id) {
        if (!parentItem.children) {
          parentItem.children = []
        }

        parentItem.children.push(item)
        addChildren(item)
      }
    })

    if (parentItem.children) {
      parentItem.children
        .sort((a: Record<string, any>, b: Record<string, any>) =>
          a.title.localeCompare(b.title),
        )
        .sort(
          (a: Record<string, any>, b: Record<string, any>) =>
            a.position - b.position,
        )
    }
  }

  const topLevelItems = items
    .filter((item) => !item.parentId)
    .sort((a, b) => a.title.localeCompare(b.title))
    .sort((a, b) => a.position - b.position)

  topLevelItems.forEach((topLevelItem) => addChildren(topLevelItem))

  return topLevelItems
}

export const fetchMenuData = async ({ id }: { id: (typeof ids)[number] }) => {
  const response: any = await fetchApi(`/menus/${id}/items-by-safe-name`)

  const items = response.items
    ?.map((item: Record<string, any>) => {
      const targetType = item.data?.target_type || item.model?.data?.target_type
      const rawUrl = normalizeMenuUrl(
        targetType === 'external_url'
          ? item.data?.external_url || item.model?.data?.external_url
          : item.data?.url || item.model?.url?.data?.url,
      )
      const external = isExternalUrl(rawUrl)

      return {
        id: item.data?.id,
        parentId: item.data?.parent_id,
        position: item.data?.position,
        title:
          item.data?.title ||
          item.model?.translation?.data?.title ||
          item.model?.data?.title ||
          item.model?.translation?.data?.name,
        url: external ? rawUrl : cleanPath('/', rawUrl),
        external,
        restrictedCountries: item.model?.limit_visibility?.data || [],
      }
    })
    .filter((item: Record<string, any>) => item.title && item.url)

  return menuSchema.parse(createMenu(items))
}

export type MenuData = Awaited<ReturnType<typeof fetchMenuData>>
